import { vec3 } from 'gl-matrix';
import { BoundingDimensions } from '../util/BoundingDimensions';

export interface BoxInertiaResult {
  inertiaDiag: vec3;
  inverseInertiaDiag: vec3;
}

/**
 * Compute the diagonal inertia tensor for a solid box of uniform density.
 * extX/Y/Z are the full extents (width, height, depth), not half-extents.
 */
export function computeBoxInertia(
  mass: number,
  extX: number,
  extY: number,
  extZ: number,
): BoxInertiaResult {
  let k = mass / 12;
  let ix = k * (extY * extY + extZ * extZ);
  let iy = k * (extX * extX + extZ * extZ);
  let iz = k * (extX * extX + extY * extY);

  return {
    inertiaDiag: vec3.fromValues(ix, iy, iz),
    inverseInertiaDiag: vec3.fromValues(
      ix > 0 ? 1 / ix : 0,
      iy > 0 ? 1 / iy : 0,
      iz > 0 ? 1 / iz : 0,
    ),
  };
}

/**
 * Compute inertia from a BoundingDimensions (box approximation).
 * Scale applies uniform scaling from the render object's TRS.
 */
export function computeFromBounds(
  mass: number,
  bounds: BoundingDimensions,
  scale: number = 1,
): {
  inverseInertiaDiag: vec3;
  centerOfMassOffset: vec3;
  localMin: vec3;
  localMax: vec3;
} {
  let dims = bounds.maxDimensions;
  let result = computeBoxInertia(mass, dims[0] * scale, dims[1] * scale, dims[2] * scale);

  // Vertex centroid as center of mass (for angular impulse lever arm)
  let offset = vec3.scale(vec3.create(), bounds.center, scale);

  // Actual local-space AABB, scaled
  let localMin = vec3.scale(vec3.create(), bounds.localMin, scale);
  let localMax = vec3.scale(vec3.create(), bounds.localMax, scale);

  return {
    inverseInertiaDiag: result.inverseInertiaDiag,
    centerOfMassOffset: offset,
    localMin,
    localMax,
  };
}
